import { useMemo } from "react";
import { useRolePermissions } from "@Front/Stores/RolePermissionsStore";
import { resolveUserContext, type ResolvedUserContext } from "@Front/Utils/userContextResolver";
import { resolveActiveAuthContextFromSession } from "@Front/Api/helpers/authTokenResolutionHelpers";
import { USER_CONTEXT_CUSTOMER, USER_CONTEXT_THIRD_PARTY } from "le-coffre-resources/dist/MemberTypes";
import { isClientContextRoute } from "@Front/Utils/routeContextRegistry";

type PermissionContextType = ReturnType<typeof useRolePermissions>["permissionContext"];

type UseResolvedUserContextResult = ResolvedUserContext & {
	permissionContext: PermissionContextType;
	contextSource: "permission_context" | "session_context_override";
	activeSessionContext: ReturnType<typeof resolveActiveAuthContextFromSession>;
};

function getBrowserPathname(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	return window.location.pathname || null;
}

type OverrideResult = {
	resolvedContext: ResolvedUserContext;
	contextSource: "permission_context" | "session_context_override";
	activeSessionContext: ReturnType<typeof resolveActiveAuthContextFromSession>;
};

function buildPermissionContextResult(
	resolvedContext: ResolvedUserContext,
	activeSessionContext: ReturnType<typeof resolveActiveAuthContextFromSession>,
): OverrideResult {
	return {
		resolvedContext,
		contextSource: "permission_context",
		activeSessionContext,
	};
}

function buildCustomerResolvedContext(isThirdParty: boolean): ResolvedUserContext {
	return {
		userContext: isThirdParty ? USER_CONTEXT_THIRD_PARTY : USER_CONTEXT_CUSTOMER,
		isThirdParty,
		isCustomer: !isThirdParty,
		isInvitedNotary: false,
	};
}

function overrideWithSessionContextWhenNeeded(
	resolvedContext: ResolvedUserContext,
): OverrideResult {
	const activeAuthContext = typeof window === "undefined" ? null : resolveActiveAuthContextFromSession();
	if (typeof window === "undefined") {
		return buildPermissionContextResult(resolvedContext, activeAuthContext);
	}
	if (!isClientContextRoute(getBrowserPathname())) {
		return buildPermissionContextResult(resolvedContext, activeAuthContext);
	}
	if (activeAuthContext === "customer") {
		return {
			resolvedContext: buildCustomerResolvedContext(false),
			contextSource: "session_context_override",
			activeSessionContext: activeAuthContext,
		};
	}
	if (activeAuthContext === "thirdparty") {
		return {
			resolvedContext: buildCustomerResolvedContext(true),
			contextSource: "session_context_override",
			activeSessionContext: activeAuthContext,
		};
	}
	return buildPermissionContextResult(resolvedContext, activeAuthContext);
}

export function useResolvedUserContext(
	overridePermissionContext?: PermissionContextType,
): UseResolvedUserContextResult {
	const { permissionContext: hookPermissionContext } = useRolePermissions();
	const effectivePermissionContext = overridePermissionContext ?? hookPermissionContext;

	return useMemo(() => {
		const resolvedContext = resolveUserContext(effectivePermissionContext);
		const effectiveResolvedContext = overrideWithSessionContextWhenNeeded(resolvedContext);
		return {
			permissionContext: effectivePermissionContext,
			...effectiveResolvedContext.resolvedContext,
			contextSource: effectiveResolvedContext.contextSource,
			activeSessionContext: effectiveResolvedContext.activeSessionContext,
		};
	}, [effectivePermissionContext]);
}
